import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { RouteContext, VerticalContext } from 'lib/ContextTypes';

import { useAmpEmbedResize } from 'lib/Hooks/useAmpEmbedResize';
import styles from './styles.module.scss';

const ExpandableList = ({
  cards = [],
  callToActionText = {
    collapsed: '',
    expanded: '',
  },
  trackDataAnalyticsClicks = () => {},
  numberOfCardsToShow = 3,
  subtitle = '',
  title = '',
}) => {
  useAmpEmbedResize();

  const vertical = React.useContext(VerticalContext);
  const route = React.useContext(RouteContext);
  const isAmp = route.path.indexOf('embedded-pkg') !== -1;

  const [isExpanded, setIsExpanded] = useState(false);

  if (!cards.length) return null;

  const hasHiddenCards = cards.length > numberOfCardsToShow;
  const numHiddenCards = hasHiddenCards ? cards.length - numberOfCardsToShow : 0;

  const containerRef = useRef();

  const getCardList = () => {
    if (!hasHiddenCards || (hasHiddenCards && isExpanded)) {
      return cards.map((c) => (
        <li key={c.props.product.id}>{c}</li>));
    }

    return [
      ...cards.slice(0, numberOfCardsToShow).map((c) => (<li key={c.props.product.id}>{c}</li>)),
      (
        <li
          className={styles.expandableListAffordance}
          data-testid="expandable-list__affordance"
          inert="true"
          key="affordance"
        >
          <div className={styles.expandableListOverlay} />
          {cards[numberOfCardsToShow]}
        </li>
      ),
      ...cards.slice(numberOfCardsToShow + 1).map((c) => (<li key={numberOfCardsToShow + c.props.product.id} style={{ display: 'none' }}>{c}</li>)),
    ];
  };

  const handleExpandListClick = ({ expand, callToAction }) => {
    setIsExpanded(expand);
    trackDataAnalyticsClicks(callToAction);
  };

  return (
    <div
      className={classNames(styles.expandableList, { [styles.expandableListAmp]: isAmp })}
      data-testid="expandable-list"
    >
      <div className={styles.expandableListHeader}>
        <div className={styles.expandableListHeaderTab}>
          {
            vertical === 'select' && (
              <h3 className={styles.expandableListTitle}>
                {title}
                {
                  title && subtitle && (
                    <span
                      className={styles.expandableListSubtitle}
                      data-testid="expandable-list__subtitle"
                    >
                      {subtitle}
                    </span>
                  )
                }
              </h3>
            )
          }
          {
            vertical === 'today' && (
              <>
                <h3 className={styles.expandableListTitle}>
                  {title}
                </h3>
                {
                  title && subtitle && (
                    <h4
                      className={styles.expandableListSubtitle}
                      data-testid="expandable-list__subtitle"
                    >
                      {subtitle}
                    </h4>
                  )
                }
              </>
            )
          }
        </div>
      </div>
      <ul
        ref={containerRef}
        className={classNames(styles.expandableListContainer,
          { [styles.expandableListContainerNoSubtitle]: !subtitle },
          { [styles.expandableListContainerNoExpand]: !hasHiddenCards })}
      >
        {getCardList()}
      </ul>
      {
        hasHiddenCards && !isExpanded && (
          <button
            className={styles.expandableListButton}
            onClick={() => handleExpandListClick({ expand: true, callToAction: 'showmore' })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsExpanded(true);
                setTimeout(() => {
                  containerRef.current
                    .querySelectorAll('li')[numberOfCardsToShow]
                    .querySelector('a')
                    .focus();
                }, 100);
              }
            }}
            type="button"
          >
            {callToActionText?.collapsed || `See ${numHiddenCards} More`}
            <span className={`${styles.icon} ${styles.iconDown} icon icon-chevron-right`} />
          </button>
        )
      }

      {
        hasHiddenCards && isExpanded && (
          <button
            className={styles.expandableListButton}
            onClick={() => handleExpandListClick({ expand: false, callToAction: 'showless' })}
            type="button"
          >
            {callToActionText?.expanded || 'See Less'}
            <span className={`${styles.icon} ${styles.iconUp} icon icon-chevron-right`} />
          </button>
        )
      }
    </div>
  );
};

export { ExpandableList };
